import { Button, Card, CardContent, Divider, Stack, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import useAuth from '../../../hooks/useAuth'
import CustomerDialog from './CustomerDialog'
import CustomersGrid from './CustomersGrid'
import CustomerApi from '../../../api/CustomerApi'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { CustomerListItem, CustomerListSortField, PaginatedResponse } from '@finance-lab/utils/types'

const customerApi = new CustomerApi()
const DEFAULT_PAGE_SIZE = 25

function AdminCustomersView() {
    const { isAuthenticated } = useAuth()
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<CustomerListItem>>({ data: [], totalCount: 0 })
    const [loading, setLoading] = useState(true)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE })
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }])
    const [open, setOpen] = useState(false)
    const triggerButtonRef = useRef<HTMLButtonElement>(null)

    const loadCustomers = useCallback(async () => {
        if (!isAuthenticated) {
            return
        }
        setLoading(true)
        try {
            const primarySort = sortModel[0]
            const response = await customerApi.adminFindCustomers({
                paginationModel,
                search: search || undefined,
                sortField: (primarySort?.field as CustomerListSortField) ?? 'name',
                sortDirection: primarySort?.sort ?? 'asc',
            })
            setPageInfo(response)
        } finally {
            setLoading(false)
        }
    }, [isAuthenticated, paginationModel, search, sortModel])

    useEffect(() => {
        void loadCustomers()
    }, [loadCustomers])

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            const nextSearch = searchInput.trim()
            setSearch((prev) => {
                if (prev === nextSearch) {
                    return prev
                }
                setPaginationModel((model) => ({ ...model, page: 0 }))
                return nextSearch
            })
        }, 300)
        return () => window.clearTimeout(timeout)
    }, [searchInput])

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel)
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }

    useEffect(() => {
        if (!loading) {
            document.title = `Kunder (${pageInfo.totalCount})`
        }
    }, [loading, pageInfo.totalCount])

    const handleOpenDialog = () => {
        setOpen(true)
    }

    const handleCloseDialog = () => {
        setOpen(false)
        if (triggerButtonRef.current) {
            triggerButtonRef.current.focus()
        }
    }

    const handleCreateCustomer = async (newCustomer: { name: string }) => {
        await customerApi.createCustomer(newCustomer.name)
        await loadCustomers()
        handleCloseDialog()
    }

    return (
        <>
            <Helmet>
                <title>Kunder</title>
            </Helmet>
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Kunder ({loading ? '...' : pageInfo.totalCount})
            </Typography>
            <Stack
                direction='row'
                spacing={2}
                sx={{
                    mb: 2,
                    alignItems: 'center',
                }}
            >
                <TextField
                    size='small'
                    label='Sök kund'
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                />
                <Button variant='contained' color='primary' onClick={handleOpenDialog} ref={triggerButtonRef}>
                    Skapa ny kund
                </Button>
            </Stack>
            <Divider />
            <Card>
                <CardContent>
                    <CustomersGrid
                        customers={pageInfo.data}
                        loading={loading}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        rowCount={pageInfo.totalCount}
                        sortingMode='server'
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                    />
                </CardContent>
            </Card>
            <CustomerDialog open={open} onClose={handleCloseDialog} onSubmit={handleCreateCustomer} />
        </>
    )
}

export default AdminCustomersView
